/**
 * European roulette rules. No DOM, no Node — the web wheel and a later Expo table
 * have to land on the same pockets and pay the same odds.
 *
 * Layout and payouts match the legacy `ruletka/utils/constants.js` table:
 * single-zero wheel, even-money outside bets, 2:1 dozens/columns, and French
 * call bets that expand into a fixed chip pattern.
 *
 * Winning number is always chosen on the server. The old client could report
 * `wheel_result`; that hole is closed here.
 */

export const RL = {
  MIN_BET: 10,
  MAX_BET: 1_000_000,
  DEFAULT_BET: 50,
  CHIP_DENOMS: [10, 50, 100, 500, 1000] as const,
  BETTING_MS: 15_000,
  SPIN_MS: 9_000,
  RESULT_MS: 5_000,
  MAX_CHAT_LENGTH: 200,
  MAX_CHAT_HISTORY: 150,
  HISTORY: 10,
} as const;

/** Clockwise from 0 at the top of the wheel, matching the physical European order. */
export const WHEEL_ORDER = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14,
  31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
] as const;

export const WHEEL_POCKETS = WHEEL_ORDER.length;
export const WHEEL_STEP_DEG = 360 / WHEEL_POCKETS;

export const RED_NUMBERS = [
  32, 19, 21, 25, 34, 27, 36, 30, 23, 5, 16, 1, 14, 9, 18, 7, 12, 3,
] as const;

export const BLACK_NUMBERS = [
  15, 4, 2, 17, 6, 13, 11, 8, 10, 24, 33, 20, 31, 22, 29, 28, 35, 26,
] as const;

export const COLUMN_FIRST = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34] as const;
export const COLUMN_SECOND = [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35] as const;
export const COLUMN_THIRD = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36] as const;

/** Top-to-bottom visual rows of the cloth (third column on top). */
export const TABLE_ROWS: readonly (readonly number[])[] = [COLUMN_THIRD, COLUMN_SECOND, COLUMN_FIRST];

export type PocketColor = "green" | "red" | "black";

export type RlBetKind =
  | "STRAIGHT_UP"
  | "RED"
  | "BLACK"
  | "EVEN"
  | "ODD"
  | "HIGH"
  | "LOW"
  | "DOZEN_FIRST"
  | "DOZEN_SECOND"
  | "DOZEN_THIRD"
  | "COLUMN_FIRST"
  | "COLUMN_SECOND"
  | "COLUMN_THIRD"
  | "SPLIT"
  | "STREET"
  | "CORNER"
  | "TRIO"
  | "SIX_LINE";

export type RlCallKind = "VOISINS_DU_ZERO" | "JEU_ZERO" | "TIERS_DU_CYLINDRE" | "ORPHELINS";

export type RlPlaceKind = RlBetKind | RlCallKind | "NEIGHBORS";

export interface RlBetPayout {
  payout: number;
  label: string;
}

export const BET_PAYOUTS: Record<RlBetKind, RlBetPayout> = {
  STRAIGHT_UP: { payout: 35, label: "Numer" },
  RED: { payout: 1, label: "Czerwone" },
  BLACK: { payout: 1, label: "Czarne" },
  EVEN: { payout: 1, label: "Parzyste" },
  ODD: { payout: 1, label: "Nieparzyste" },
  HIGH: { payout: 1, label: "19–36" },
  LOW: { payout: 1, label: "1–18" },
  DOZEN_FIRST: { payout: 2, label: "1–12" },
  DOZEN_SECOND: { payout: 2, label: "13–24" },
  DOZEN_THIRD: { payout: 2, label: "25–36" },
  COLUMN_FIRST: { payout: 2, label: "Kolumna 1" },
  COLUMN_SECOND: { payout: 2, label: "Kolumna 2" },
  COLUMN_THIRD: { payout: 2, label: "Kolumna 3" },
  SPLIT: { payout: 17, label: "Split" },
  STREET: { payout: 11, label: "Street" },
  CORNER: { payout: 8, label: "Corner" },
  TRIO: { payout: 11, label: "Trio" },
  SIX_LINE: { payout: 5, label: "Linia" },
};

export const CALL_LABELS: Record<RlCallKind, string> = {
  VOISINS_DU_ZERO: "Voisins",
  JEU_ZERO: "Jeu zéro",
  TIERS_DU_CYLINDRE: "Tiers",
  ORPHELINS: "Orphelins",
};

interface CallUnit {
  kind: RlBetKind;
  numbers: readonly number[];
  chips: number;
}

export const CALL_BETS: Record<RlCallKind, readonly CallUnit[]> = {
  VOISINS_DU_ZERO: [
    { kind: "TRIO", numbers: [0, 2, 3], chips: 2 },
    { kind: "SPLIT", numbers: [4, 7], chips: 1 },
    { kind: "SPLIT", numbers: [12, 15], chips: 1 },
    { kind: "SPLIT", numbers: [18, 21], chips: 1 },
    { kind: "SPLIT", numbers: [19, 22], chips: 1 },
    { kind: "SPLIT", numbers: [32, 35], chips: 1 },
    { kind: "CORNER", numbers: [25, 26, 28, 29], chips: 2 },
  ],
  JEU_ZERO: [
    { kind: "SPLIT", numbers: [0, 3], chips: 1 },
    { kind: "SPLIT", numbers: [12, 15], chips: 1 },
    { kind: "SPLIT", numbers: [32, 35], chips: 1 },
    { kind: "STRAIGHT_UP", numbers: [26], chips: 1 },
  ],
  TIERS_DU_CYLINDRE: [
    { kind: "SPLIT", numbers: [5, 8], chips: 1 },
    { kind: "SPLIT", numbers: [10, 11], chips: 1 },
    { kind: "SPLIT", numbers: [13, 16], chips: 1 },
    { kind: "SPLIT", numbers: [23, 24], chips: 1 },
    { kind: "SPLIT", numbers: [27, 30], chips: 1 },
    { kind: "SPLIT", numbers: [33, 36], chips: 1 },
  ],
  ORPHELINS: [
    { kind: "STRAIGHT_UP", numbers: [1], chips: 1 },
    { kind: "SPLIT", numbers: [6, 9], chips: 1 },
    { kind: "SPLIT", numbers: [14, 17], chips: 1 },
    { kind: "SPLIT", numbers: [17, 20], chips: 1 },
    { kind: "SPLIT", numbers: [31, 34], chips: 1 },
  ],
};

export const CALL_UNITS: Record<RlCallKind, number> = {
  VOISINS_DU_ZERO: 9,
  JEU_ZERO: 4,
  TIERS_DU_CYLINDRE: 6,
  ORPHELINS: 5,
};

const RED = new Set<number>(RED_NUMBERS);
const BLACK = new Set<number>(BLACK_NUMBERS);

export function colorOf(n: number): PocketColor {
  if (n === 0) return "green";
  if (RED.has(n)) return "red";
  return "black";
}

export function isPocket(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= 36;
}

export function pocketIndex(n: number): number {
  return WHEEL_ORDER.indexOf(n as (typeof WHEEL_ORDER)[number]);
}

/**
 * CSS degrees to spin so pocket `n` sits under a 12 o'clock pointer.
 * Pockets are drawn clockwise from the top starting at 0.
 */
export function wheelRotationFor(n: number, extraTurns = 5, previous = 0): number {
  const index = pocketIndex(n);
  const landing = index < 0 ? 0 : -(index * WHEEL_STEP_DEG + WHEEL_STEP_DEG / 2);
  const base = Math.ceil((previous + 0.01) / 360) * 360;
  return base + extraTurns * 360 + landing;
}

export function clampRlBet(amount: number): number {
  if (!Number.isFinite(amount)) return RL.MIN_BET;
  return Math.min(RL.MAX_BET, Math.max(RL.MIN_BET, Math.floor(amount)));
}

export function isCallKind(kind: string): kind is RlCallKind {
  return kind in CALL_BETS;
}

export function isBetKind(kind: string): kind is RlBetKind {
  return kind in BET_PAYOUTS;
}

export function isNeighborsKind(kind: string): kind is "NEIGHBORS" {
  return kind === "NEIGHBORS";
}

export function isPlaceKind(kind: string): kind is RlPlaceKind {
  return isBetKind(kind) || isCallKind(kind) || isNeighborsKind(kind);
}

/** Wheel-order neighbors: `n` plus `span` pockets each side. */
export function neighborsOf(n: number, span = 2): number[] {
  const idx = pocketIndex(n);
  if (idx < 0) return [];
  const out: number[] = [];
  for (let d = -span; d <= span; d += 1) {
    const pocket = WHEEL_ORDER[(idx + d + WHEEL_POCKETS) % WHEEL_POCKETS];
    if (pocket !== undefined) out.push(pocket);
  }
  return out;
}

export function expandNeighbors(center: number, totalAmount: number, span = 2): ExpandedBet[] {
  const numbers = neighborsOf(center, span);
  const stake = clampRlBet(totalAmount);
  if (numbers.length === 0) return [];
  const out: ExpandedBet[] = [];
  let remaining = stake;
  for (let i = 0; i < numbers.length; i += 1) {
    const n = numbers[i];
    if (n === undefined) continue;
    const isLast = i === numbers.length - 1;
    const amount = isLast ? remaining : Math.floor(stake / numbers.length);
    remaining -= amount;
    if (amount <= 0) continue;
    out.push({
      kind: "STRAIGHT_UP",
      amount,
      numbers: [n],
      payout: BET_PAYOUTS.STRAIGHT_UP.payout,
    });
  }
  return out;
}

function sortedUnique(numbers: readonly number[]): number[] {
  return [...new Set(numbers)].sort((a, b) => a - b);
}

function sameStreet(a: number, b: number): boolean {
  return a >= 1 && b >= 1 && Math.floor((a - 1) / 3) === Math.floor((b - 1) / 3);
}

export function isValidSplit(numbers: readonly number[]): boolean {
  const [a, b] = sortedUnique(numbers);
  if (a === undefined || b === undefined || numbers.length !== 2) return false;
  if (a === 0) return b === 1 || b === 2 || b === 3;
  if (b - a === 3) return true;
  return b - a === 1 && sameStreet(a, b);
}

export function isValidStreet(numbers: readonly number[]): boolean {
  const [a, b, c] = sortedUnique(numbers);
  if (a === undefined || b === undefined || c === undefined || numbers.length !== 3) return false;
  if (a === 0) return false;
  return b - a === 1 && c - b === 1 && sameStreet(a, c);
}

export function isValidCorner(numbers: readonly number[]): boolean {
  const [a, b, c, d] = sortedUnique(numbers);
  if (a === undefined || b === undefined || c === undefined || d === undefined || numbers.length !== 4) {
    return false;
  }
  return b - a === 1 && c - a === 3 && d - b === 3 && d - c === 1;
}

export function isValidTrio(numbers: readonly number[]): boolean {
  const [a, b, c] = sortedUnique(numbers);
  if (a === undefined || b === undefined || c === undefined || numbers.length !== 3) return false;
  return (a === 0 && b === 1 && c === 2) || (a === 0 && b === 2 && c === 3);
}

export function isValidSixLine(numbers: readonly number[]): boolean {
  const sorted = sortedUnique(numbers);
  if (sorted.length !== 6) return false;
  const first = sorted.slice(0, 3);
  const second = sorted.slice(3);
  const a = first[0];
  const d = second[0];
  return a !== undefined && d === a + 3 && isValidStreet(first) && isValidStreet(second);
}

export type BetValidation =
  | { ok: true; numbers: number[] }
  | { ok: false; message: string };

export function validateBet(kind: RlBetKind, numbers: readonly number[] = []): BetValidation {
  switch (kind) {
    case "STRAIGHT_UP": {
      const n = numbers[0];
      if (numbers.length !== 1 || n === undefined || !isPocket(n)) {
        return { ok: false, message: "Wybierz numer od 0 do 36" };
      }
      return { ok: true, numbers: [n] };
    }
    case "RED":
    case "BLACK":
    case "EVEN":
    case "ODD":
    case "HIGH":
    case "LOW":
      return { ok: true, numbers: [] };
    case "DOZEN_FIRST":
      return { ok: true, numbers: Array.from({ length: 12 }, (_, i) => i + 1) };
    case "DOZEN_SECOND":
      return { ok: true, numbers: Array.from({ length: 12 }, (_, i) => i + 13) };
    case "DOZEN_THIRD":
      return { ok: true, numbers: Array.from({ length: 12 }, (_, i) => i + 25) };
    case "COLUMN_FIRST":
      return { ok: true, numbers: [...COLUMN_FIRST] };
    case "COLUMN_SECOND":
      return { ok: true, numbers: [...COLUMN_SECOND] };
    case "COLUMN_THIRD":
      return { ok: true, numbers: [...COLUMN_THIRD] };
    case "SPLIT":
      if (!isValidSplit(numbers)) return { ok: false, message: "Split musi być na dwóch sąsiadach" };
      return { ok: true, numbers: sortedUnique(numbers) };
    case "STREET":
      if (!isValidStreet(numbers)) return { ok: false, message: "Street to trzy numery w kolumnie" };
      return { ok: true, numbers: sortedUnique(numbers) };
    case "CORNER":
      if (!isValidCorner(numbers)) return { ok: false, message: "Corner to kwadrat 2×2" };
      return { ok: true, numbers: sortedUnique(numbers) };
    case "TRIO":
      if (!isValidTrio(numbers)) return { ok: false, message: "Trio to 0-1-2 albo 0-2-3" };
      return { ok: true, numbers: sortedUnique(numbers) };
    case "SIX_LINE":
      if (!isValidSixLine(numbers)) return { ok: false, message: "Linia to dwa sąsiednie street'y" };
      return { ok: true, numbers: sortedUnique(numbers) };
  }
}

export interface ExpandedBet {
  kind: RlBetKind;
  amount: number;
  numbers: number[];
  payout: number;
  parentCallBet?: RlCallKind;
}

export function expandCallBet(kind: RlCallKind, totalAmount: number): ExpandedBet[] {
  const units = CALL_BETS[kind];
  const unitCount = CALL_UNITS[kind];
  const stake = clampRlBet(totalAmount);
  const out: ExpandedBet[] = [];
  let remaining = stake;
  for (let i = 0; i < units.length; i += 1) {
    const unit = units[i];
    if (!unit) continue;
    const isLast = i === units.length - 1;
    const amount = isLast ? remaining : Math.floor((unit.chips / unitCount) * stake);
    remaining -= amount;
    if (amount <= 0) continue;
    const checked = validateBet(unit.kind, unit.numbers);
    if (!checked.ok) continue;
    out.push({
      kind: unit.kind,
      amount,
      numbers: checked.numbers,
      payout: BET_PAYOUTS[unit.kind].payout,
      parentCallBet: kind,
    });
  }
  return out;
}

export function planBet(kind: RlPlaceKind, amount: number, numbers: readonly number[] = []): ExpandedBet[] | null {
  const stake = clampRlBet(amount);
  if (isCallKind(kind)) {
    const expanded = expandCallBet(kind, stake);
    return expanded.length > 0 ? expanded : null;
  }
  if (isNeighborsKind(kind)) {
    const center = numbers[0];
    if (center === undefined || numbers.length !== 1 || !isPocket(center)) return null;
    const expanded = expandNeighbors(center, stake);
    return expanded.length > 0 ? expanded : null;
  }
  const checked = validateBet(kind, numbers);
  if (!checked.ok) return null;
  return [
    {
      kind,
      amount: stake,
      numbers: checked.numbers,
      payout: BET_PAYOUTS[kind].payout,
    },
  ];
}

export function isBetWinner(kind: RlBetKind, numbers: readonly number[], winningNumber: number): boolean {
  switch (kind) {
    case "STRAIGHT_UP":
    case "SPLIT":
    case "STREET":
    case "CORNER":
    case "TRIO":
    case "SIX_LINE":
    case "DOZEN_FIRST":
    case "DOZEN_SECOND":
    case "DOZEN_THIRD":
    case "COLUMN_FIRST":
    case "COLUMN_SECOND":
    case "COLUMN_THIRD":
      return numbers.includes(winningNumber);
    case "RED":
      return RED.has(winningNumber);
    case "BLACK":
      return BLACK.has(winningNumber);
    case "EVEN":
      return winningNumber !== 0 && winningNumber % 2 === 0;
    case "ODD":
      return winningNumber % 2 === 1;
    case "HIGH":
      return winningNumber >= 19 && winningNumber <= 36;
    case "LOW":
      return winningNumber >= 1 && winningNumber <= 18;
  }
}

/** Stake returned plus odds. A 10-chip 35:1 win pays 360. */
export function payoutAmount(amount: number, payout: number, won: boolean): number {
  if (!won) return 0;
  return amount * (payout + 1);
}

export function pickPocket(rng: () => number = Math.random): number {
  return Math.floor(rng() * 37);
}

export function betLabel(kind: RlPlaceKind, numbers: readonly number[] = []): string {
  if (isCallKind(kind)) return CALL_LABELS[kind];
  if (kind === "NEIGHBORS") {
    return numbers[0] === undefined ? "Sąsiedzi" : `Sąsiedzi ${numbers[0]}`;
  }
  if (kind === "STRAIGHT_UP") return numbers[0] === undefined ? "Numer" : String(numbers[0]);
  return BET_PAYOUTS[kind].label;
}

export function betSpotKey(bet: {
  kind: RlBetKind;
  numbers: readonly number[];
  parentCallBet?: RlCallKind;
}): string {
  if (bet.parentCallBet) return `call:${bet.parentCallBet}`;
  if (bet.kind === "STRAIGHT_UP") return `n:${bet.numbers[0] ?? 0}`;
  if (
    bet.kind === "RED" ||
    bet.kind === "BLACK" ||
    bet.kind === "EVEN" ||
    bet.kind === "ODD" ||
    bet.kind === "HIGH" ||
    bet.kind === "LOW" ||
    bet.kind === "DOZEN_FIRST" ||
    bet.kind === "DOZEN_SECOND" ||
    bet.kind === "DOZEN_THIRD" ||
    bet.kind === "COLUMN_FIRST" ||
    bet.kind === "COLUMN_SECOND" ||
    bet.kind === "COLUMN_THIRD"
  ) {
    return `bet:${bet.kind}`;
  }
  return `${bet.kind}:${sortedUnique(bet.numbers).join(",")}`;
}

export interface ReplayBet {
  kind: RlPlaceKind;
  amount: number;
  numbers: number[];
}

/** Collapse a round of public bets back into placeable clicks (call bets stay bundled). */
export function replayableBets(bets: readonly RlPublicBet[]): ReplayBet[] {
  const calls = new Map<RlCallKind, number>();
  const out: ReplayBet[] = [];
  for (const bet of bets) {
    if (bet.parentCallBet) {
      calls.set(bet.parentCallBet, (calls.get(bet.parentCallBet) ?? 0) + bet.amount);
      continue;
    }
    out.push({ kind: bet.kind, amount: bet.amount, numbers: [...bet.numbers] });
  }
  for (const [kind, amount] of calls) {
    out.push({ kind, amount, numbers: [] });
  }
  return out;
}

export type InsideSpotShape = "split-v" | "split-h" | "corner" | "street" | "six";

export interface InsideGridSpot {
  kind: "SPLIT" | "STREET" | "CORNER" | "SIX_LINE";
  numbers: number[];
  col: number;
  row: number;
  shape: InsideSpotShape;
}

export interface InsideZeroSpot {
  kind: "SPLIT" | "TRIO";
  numbers: number[];
}

function streetNumbers(col: number): number[] {
  return [1 + 3 * col, 2 + 3 * col, 3 + 3 * col];
}

function numberAt(col: number, visualRow: number): number {
  return 3 - visualRow + 3 * col;
}

function buildInsideSpots(): { grid: InsideGridSpot[]; zero: InsideZeroSpot[] } {
  const grid: InsideGridSpot[] = [];
  for (let col = 0; col < 12; col += 1) {
    grid.push({ kind: "SPLIT", numbers: [numberAt(col, 0), numberAt(col, 1)], col, row: 1, shape: "split-v" });
    grid.push({ kind: "SPLIT", numbers: [numberAt(col, 1), numberAt(col, 2)], col, row: 2, shape: "split-v" });
    grid.push({ kind: "STREET", numbers: streetNumbers(col), col, row: 3, shape: "street" });
  }
  for (let col = 1; col < 12; col += 1) {
    for (let row = 0; row < 3; row += 1) {
      grid.push({
        kind: "SPLIT",
        numbers: [numberAt(col - 1, row), numberAt(col, row)],
        col,
        row,
        shape: "split-h",
      });
    }
    grid.push({
      kind: "CORNER",
      numbers: [numberAt(col - 1, 0), numberAt(col, 0), numberAt(col - 1, 1), numberAt(col, 1)],
      col,
      row: 1,
      shape: "corner",
    });
    grid.push({
      kind: "CORNER",
      numbers: [numberAt(col - 1, 1), numberAt(col, 1), numberAt(col - 1, 2), numberAt(col, 2)],
      col,
      row: 2,
      shape: "corner",
    });
    grid.push({
      kind: "SIX_LINE",
      numbers: [...streetNumbers(col - 1), ...streetNumbers(col)],
      col,
      row: 3,
      shape: "six",
    });
  }
  const zero: InsideZeroSpot[] = [
    { kind: "SPLIT", numbers: [0, 3] },
    { kind: "TRIO", numbers: [0, 2, 3] },
    { kind: "SPLIT", numbers: [0, 2] },
    { kind: "TRIO", numbers: [0, 1, 2] },
    { kind: "SPLIT", numbers: [0, 1] },
  ];
  return { grid, zero };
}

const INSIDE = buildInsideSpots();
export const INSIDE_GRID_SPOTS: readonly InsideGridSpot[] = INSIDE.grid;
export const INSIDE_ZERO_SPOTS: readonly InsideZeroSpot[] = INSIDE.zero;

export const RL_PLAYER_COLORS = [
  "#ff6b6b",
  "#4ecdc4",
  "#45b7d1",
  "#96c93d",
  "#feca57",
  "#ff9ff3",
  "#54a0ff",
  "#5f27cd",
  "#00d2d3",
  "#ff9f43",
] as const;

export function playerColor(userId: number): string {
  const color = RL_PLAYER_COLORS[Math.abs(userId) % RL_PLAYER_COLORS.length];
  return color ?? "#ff6b6b";
}

export type RlPhase = "betting" | "spinning" | "results";

export interface RlPublicBet {
  id: string;
  userId: number;
  playerName: string;
  kind: RlBetKind;
  amount: number;
  numbers: number[];
  payout: number;
  parentCallBet?: RlCallKind;
}

export interface RlPublicPlayer {
  userId: number;
  name: string;
  chips: number;
  keys: number;
  seated: boolean;
  color: string;
}

export interface RlWinner {
  userId: number;
  playerName: string;
  kind: RlBetKind;
  amount: number;
  winnings: number;
  netProfit: number;
}

export interface RlGameUpdate {
  phase: RlPhase;
  roundNumber: number;
  players: RlPublicPlayer[];
  bets: RlPublicBet[];
  lastWinningNumbers: number[];
  winningNumber: number | null;
  bettingEndsAt: number | null;
  spinEndsAt: number | null;
  resultEndsAt: number | null;
  winners: RlWinner[];
}

export type RlChatMessage = {
  user: string;
  text: string;
  ts: number;
  cid?: string;
  role?: "dealer" | "player";
  isDealer?: boolean;
};
